import { useState, useCallback, useEffect } from 'react';
import fr from './fr.json';
import en from './en.json';

export type Locale = 'fr' | 'en';

const STORAGE_KEY = 'helios-locale';
const DEFAULT_LOCALE: Locale = 'fr';
const SUPPORTED_LOCALES: Locale[] = ['fr', 'en'];

type TranslationDict = Record<string, unknown>;

const translations: Record<Locale, TranslationDict> = { fr, en };

function getStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (stored && SUPPORTED_LOCALES.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage unavailable
  }
  return DEFAULT_LOCALE;
}

// Simple global state to share locale across hooks without context
let globalLocale: Locale = getStoredLocale();
const listeners = new Set<(locale: Locale) => void>();

function setGlobalLocale(locale: Locale) {
  if (!SUPPORTED_LOCALES.includes(locale)) return;
  globalLocale = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // localStorage unavailable
  }
  listeners.forEach((cb) => cb(locale));
}

/**
 * Resolve a dot-notation key from a nested object.
 * e.g. t('nav.dashboard') => translations[locale].nav.dashboard
 */
function resolve(obj: TranslationDict, key: string): string {
  const parts = key.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return key;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : key;
}

export function useTranslation() {
  const [locale, setLocaleState] = useState<Locale>(globalLocale);

  useEffect(() => {
    const handler = (newLocale: Locale) => setLocaleState(newLocale);
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }, []);

  const setLocale = useCallback((newLocale: Locale) => {
    setGlobalLocale(newLocale);
  }, []);

  const t = useCallback(
    (key: string): string => {
      return resolve(translations[locale], key);
    },
    [locale],
  );

  return { t, locale, setLocale };
}

export { SUPPORTED_LOCALES, DEFAULT_LOCALE };
